import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { X, Crop as CropIcon, Camera, Loader2 } from 'lucide-react';

type Rect = { x: number; y: number; w: number; h: number };

type Props = {
  /** Called with the cropped PNG blob. The cropper then closes itself. */
  onCropped: (blob: Blob) => void | Promise<void>;
  onCancel: () => void;
};

/**
 * Two-step modal:
 *   1. capturing  → invoke getDisplayMedia, grab one frame, stash in a hidden
 *                   canvas, then move to crop step. Browser stops sharing.
 *   2. cropping   → show the captured image; user drags a rectangle on it.
 *                   "Crop & attach" extracts the region and hands the PNG blob
 *                   to onCropped().
 *
 * The rect lives in image-display coords (post-letterboxing). We scale to the
 * natural canvas dimensions when extracting so we get full-resolution crops.
 */
export function ScreenshotCropper({ onCropped, onCancel }: Props) {
  const [phase, setPhase] = useState<'capturing' | 'cropping' | 'extracting'>('capturing');
  const [error, setError] = useState<string | null>(null);
  const [imgUrl, setImgUrl] = useState<string | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);

  // The full-resolution canvas of the captured screen — kept for the crop extract.
  const captureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // The <img> shown in the crop UI — used to translate pointer coords.
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragOriginRef = useRef<{ x: number; y: number } | null>(null);

  // ── Capture: invoke getDisplayMedia once on mount ──────────────────────
  useEffect(() => {
    let cancelled = false;

    async function capture() {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const stream: MediaStream = await (navigator.mediaDevices as any).getDisplayMedia({
          video: { displaySurface: 'browser' },
          audio: false,
          // Chrome-specific hint for "this tab" picker behavior.
          preferCurrentTab: true,
          selfBrowserSurface: 'include',
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const video = document.createElement('video');
        video.srcObject = stream;
        video.muted = true;
        await video.play();

        // One frame's worth.
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Canvas 2D context unavailable');
        ctx.drawImage(video, 0, 0);

        stream.getTracks().forEach((t) => t.stop());
        video.srcObject = null;

        if (cancelled) return;
        captureCanvasRef.current = canvas;
        const url = canvas.toDataURL('image/png');
        setImgUrl(url);
        setPhase('cropping');
      } catch (err) {
        if (cancelled) return;
        // User-cancelled the share picker → NotAllowedError. Treat as cancel,
        // not as a real failure; just close the modal cleanly.
        const name = err instanceof Error ? err.name : '';
        if (name === 'NotAllowedError' || name === 'AbortError') {
          onCancel();
          return;
        }
        setError(err instanceof Error ? err.message : 'Capture failed');
      }
    }
    void capture();

    return () => {
      cancelled = true;
    };
  }, [onCancel]);

  // ── Crop interaction ───────────────────────────────────────────────────
  function pointFromEvent(e: PointerEvent<HTMLDivElement>): { x: number; y: number } | null {
    const el = imgRef.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(r.width, e.clientX - r.left)),
      y: Math.max(0, Math.min(r.height, e.clientY - r.top)),
    };
  }

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    if (phase !== 'cropping') return;
    const p = pointFromEvent(e);
    if (!p) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragOriginRef.current = p;
    setRect({ x: p.x, y: p.y, w: 0, h: 0 });
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    if (phase !== 'cropping' || !dragOriginRef.current) return;
    const p = pointFromEvent(e);
    if (!p) return;
    const o = dragOriginRef.current;
    setRect({
      x: Math.min(o.x, p.x),
      y: Math.min(o.y, p.y),
      w: Math.abs(p.x - o.x),
      h: Math.abs(p.y - o.y),
    });
  }

  function onPointerUp(e: PointerEvent<HTMLDivElement>) {
    if (phase !== 'cropping') return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragOriginRef.current = null;
    // rect stays in state — user can adjust by dragging again or commit
  }

  // ── Extract the crop and hand off as PNG blob ──────────────────────────
  async function commitCrop() {
    const canvas = captureCanvasRef.current;
    const imgEl = imgRef.current;
    if (!canvas || !imgEl) return;

    setPhase('extracting');
    setError(null);

    // Translate display coords → natural canvas coords.
    const dispW = imgEl.clientWidth || imgEl.naturalWidth;
    const dispH = imgEl.clientHeight || imgEl.naturalHeight;
    const sx = canvas.width / dispW;
    const sy = canvas.height / dispH;

    // If no rect was drawn, use the full image.
    const r = rect && rect.w > 5 && rect.h > 5
      ? rect
      : { x: 0, y: 0, w: dispW, h: dispH };

    const out = document.createElement('canvas');
    out.width = Math.round(r.w * sx);
    out.height = Math.round(r.h * sy);
    const ctx = out.getContext('2d');
    if (!ctx) {
      setError('Canvas 2D context unavailable');
      setPhase('cropping');
      return;
    }
    ctx.drawImage(
      canvas,
      Math.round(r.x * sx), Math.round(r.y * sy),
      Math.round(r.w * sx), Math.round(r.h * sy),
      0, 0, out.width, out.height,
    );

    const blob = await new Promise<Blob | null>((resolve) =>
      out.toBlob((b) => resolve(b), 'image/png'),
    );
    if (!blob) {
      setError('Failed to encode PNG');
      setPhase('cropping');
      return;
    }
    await onCropped(blob);
  }

  // During the capture phase, render NOTHING so the page underneath is
  // fully visible — that's what the user is actually trying to screenshot.
  // The browser's own native picker chrome is shown by Chrome/Firefox, not us.
  if (phase === 'capturing') return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70">
      <div className="bg-card text-card-foreground rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <Camera className="h-4 w-4" />
            {phase === 'cropping' && 'Drag to select a region'}
            {phase === 'extracting' && 'Preparing…'}
          </h3>
          <button onClick={onCancel} className="text-muted-foreground hover:text-foreground" aria-label="Cancel">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 bg-muted/30 flex items-center justify-center">
          {imgUrl && (
            <div
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              className="relative inline-block touch-none select-none cursor-crosshair"
            >
              <img
                ref={imgRef}
                src={imgUrl}
                alt="captured screen"
                className="max-w-full max-h-[70vh] block pointer-events-none"
                draggable={false}
              />
              {rect && rect.w > 0 && rect.h > 0 && (
                <div
                  className="absolute border-2 border-primary bg-primary/10 pointer-events-none"
                  style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
                />
              )}
            </div>
          )}
        </div>

        {error && <p className="px-5 py-2 text-sm text-destructive border-t">{error}</p>}

        {(
          <div className="flex items-center justify-between px-5 py-3 border-t bg-muted/20">
            <div className="text-xs text-muted-foreground">
              {rect && rect.w > 5 && rect.h > 5
                ? `Selection: ${Math.round(rect.w)} × ${Math.round(rect.h)}px`
                : 'No selection — full screenshot will be used.'}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onCancel}
                className="text-xs text-muted-foreground hover:text-foreground px-2 py-1"
              >
                Cancel
              </button>
              <button
                onClick={commitCrop}
                disabled={phase === 'extracting'}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-sm hover:bg-primary/90 disabled:opacity-50"
              >
                {phase === 'extracting' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CropIcon className="h-3.5 w-3.5" />
                )}
                {phase === 'extracting' ? 'Attaching…' : 'Crop & attach'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Feature-detect — falls back gracefully on older browsers. */
export function canCaptureScreen(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof (navigator.mediaDevices as { getDisplayMedia?: unknown }).getDisplayMedia === 'function'
  );
}
